import React from 'react';
import * as SurveyJs from 'survey-react';
import { useOnboardingState } from '../../hooks/onboarding/useOnboardingState';
import { useSurveyHandlers } from '../../hooks/onboarding/useSurveyHandlers';
import { useSurveyPageEffects } from '../../hooks/onboarding/useSurveyPageEffects';
import { useSurveyTracking } from '../../hooks/onboarding/useSurveyTracking';
import { OnboardingSurveyProps } from '../../types/onboarding/survey';
import { SURVEY_TRIGGER, SurveyOptions, SurveySender } from '../../types/surveys';
import { OnboardingStates } from './OnboardingStates';

import { SurveyRenderer } from '../Survey/SurveyRenderer';
import './OnboardingSurvey.scss';

SurveyJs.StylesManager.applyTheme('default');

export const OnboardingSurvey: React.FC<OnboardingSurveyProps> = ({
  onSurveyComplete,
  onSurveyStart,
  onError,
}) => {
  const {
    state,
    setState,
    showIntroHandler,
    surveyName,
    surveyRequestData,
    surveyTrigger,
    refetchStage,
  } = useOnboardingState(onError);

  const { trackQuestionAnswered, trackSurveyFinished } = useSurveyTracking(
    surveyName,
    surveyRequestData,
    surveyTrigger,
    state.onboardingData,
    onSurveyStart,
  );

  const handlers = useSurveyHandlers(
    setState.setOnboardingFinished,
    setState.setOnboardingAnswers,
    state.menopauseSymptoms,
    setState.setMenopauseSymptoms,
    onSurveyComplete,
    onError,
    refetchStage,
    trackSurveyFinished,
  );

  const { handleAfterRenderPage } = useSurveyPageEffects();

  const enhancedHandlers = {
    ...handlers,
    handleCurrentPageChanging: (sender: SurveySender, options: SurveyOptions) => {
      handlers.handleCurrentPageChanging(sender, options);

      const { currentPage } = sender;
      const questions = currentPage.elements;
      const questionAnswered = questions.find(question => question.value !== undefined);

      if (questionAnswered) {
        trackQuestionAnswered(questionAnswered, sender);
      }
    },
  };

  return (
    <div className="onboarding-flow">
      <OnboardingStates state={state} showIntroHandler={showIntroHandler} />

      {!state.showIntro && !state.onboardingFinished && (
        <div className="onboarding-survey-container survey-block-container">
          <SurveyRenderer
            surveys={state.onboardingData}
            surveyName={surveyName}
            handleAfterRenderPage={handleAfterRenderPage}
            handleCurrentPageChanged={enhancedHandlers.handleCurrentPageChanged}
            handleCurrentPageChanging={enhancedHandlers.handleCurrentPageChanging}
            handleValueChanged={enhancedHandlers.handleValueChanged}
            onSurveyComplete={enhancedHandlers.handleSurveyComplete}
            isDueDateOTC={[
              SURVEY_TRIGGER.IM_READY_PATH_END,
              SURVEY_TRIGGER.IM_READY_MY_PATH,
              SURVEY_TRIGGER.IM_READY_GUIDED_PATHS,
            ].includes(surveyTrigger)}
          />
        </div>
      )}
    </div>
  );
};
